
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  CheckCircle,
  Package,
  Truck,
  Home,
  ArrowRight,
  Calendar,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  Download,
  Printer,
} from "lucide-react"
import { useOrder } from "../../context/order-context"
import Breadcrumbs from "../../components/goods/bread-crumb"

export default function OrderConfirmationPage() {
  const { orderId } = useParams()
  const { getOrderById } = useOrder()
  const [order, setOrder] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (orderId) {
      // Simulate loading
      setIsLoading(true)
      setTimeout(() => {
        const orderData = getOrderById(orderId)
        setOrder(orderData)
        setIsLoading(false)
      }, 800)
    }
  }, [orderId, getOrderById])

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col items-center justify-center">
            <div className="w-16 h-16 border-4 border-[#ff9906] border-t-transparent rounded-full animate-spin mb-6"></div>
            <h2 className="text-xl font-semibold text-gray-700">Loading your order details...</h2>
          </div>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Order Not Found</h2>
        <p className="mb-6">The order you're looking for doesn't exist or has been removed.</p>
        <Link to="/goods" className="bg-[#ff9906] text-white px-4 py-2 rounded-md hover:bg-[#e68a00] transition-colors">
          Return to Home
        </Link>
      </div>
    )
  }

  // Format date for display
  const orderDate = new Date(order.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

  // Calculate estimated delivery date (5 days from order date)
  const deliveryDate = new Date(order.createdAt)
  deliveryDate.setDate(deliveryDate.getDate() + 5)
  const estimatedDelivery = deliveryDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumbs items={[{ label: "Orders", href: "/goods/orders" }, { label: `Order ${orderId.slice(-8)}` }]} />

      <div className="max-w-4xl mx-auto">
        {/* Success Banner */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 mb-8 shadow-sm border border-green-100">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-3 text-gray-800">Thank You for Your Order!</h1>
            <p className="text-lg text-gray-600 mb-4">Your order has been received and is being processed.</p>
            <div className="inline-block bg-white px-4 py-2 rounded-md shadow-sm border border-gray-200">
              <span className="text-gray-500 mr-2">Order Number:</span>
              <span className="font-bold text-gray-800">{order.id.slice(-8)}</span>
            </div>
          </div>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
            <div className="flex space-x-3">
              <button className="text-gray-600 hover:text-gray-800 flex items-center text-sm">
                <Printer className="h-4 w-4 mr-1" />
                Print
              </button>
              <button className="text-gray-600 hover:text-gray-800 flex items-center text-sm">
                <Download className="h-4 w-4 mr-1" />
                Download
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div className="flex items-start">
                <Calendar className="h-5 w-5 text-[#ff9906] mt-0.5 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-700">Order Date</h3>
                  <p className="text-gray-600">{orderDate}</p>
                </div>
              </div>
              <div className="flex items-start">
                <CreditCard className="h-5 w-5 text-[#ff9906] mt-0.5 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-700">Payment Method</h3>
                  <p className="text-gray-600">
                    {order.payment.method === "cash" ? "Cash on Delivery" : order.payment.method}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Truck className="h-5 w-5 text-[#ff9906] mt-0.5 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-700">Estimated Delivery</h3>
                  <p className="text-gray-600">{estimatedDelivery}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-[#ff9906] mt-0.5 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-700">Shipping Address</h3>
                  <p className="text-gray-600">
                    {order.customer.firstName} {order.customer.lastName}
                    <br />
                    {order.shipping.address}
                    <br />
                    {order.shipping.city}, {order.shipping.state} {order.shipping.zipCode}
                    <br />
                    {order.shipping.country}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-[#ff9906] mt-0.5 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-700">Email</h3>
                  <p className="text-gray-600">{order.customer.email}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Phone className="h-5 w-5 text-[#ff9906] mt-0.5 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-700">Phone</h3>
                  <p className="text-gray-600">{order.customer.phone}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-medium text-gray-800 mb-4">Order Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Product
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Price
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Quantity
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {order.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">${item.price.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{item.quantity}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        ${item.total.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                      Subtotal
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                      ${order.totals.subtotal.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                      Shipping
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                      {order.totals.shipping === 0 ? "Free" : `$${order.totals.shipping.toFixed(2)}`}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                      Tax
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                      ${order.totals.tax.toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                      Total
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-bold text-[#ff9906]">
                      ${order.totals.total.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Order Timeline */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold mb-6 text-gray-800">Order Timeline</h2>

          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            <div className="relative flex items-start mb-8">
              <div className="flex items-center justify-center w-8 h-8 bg-[#ff9906] rounded-full mr-4 z-10">
                <Package className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Order Placed</h3>
                <p className="text-sm text-gray-500">
                  {new Date(order.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
                <p className="text-sm text-gray-600 mt-1">Your order has been received and is being processed.</p>
              </div>
            </div>

            <div className="relative flex items-start mb-8">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full mr-4 z-10">
                <Truck className="h-4 w-4 text-gray-500" />
              </div>
              <div>
                <h3 className="font-medium text-gray-500">Shipped</h3>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-sm text-gray-500 mt-1">Your order will be shipped soon.</p>
              </div>
            </div>

            <div className="relative flex items-start">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full mr-4 z-10">
                <Home className="h-4 w-4 text-gray-500" />
              </div>
              <div>
                <h3 className="font-medium text-gray-500">Delivered</h3>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-sm text-gray-500 mt-1">Estimated delivery by {estimatedDelivery}.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link to="/goods/orders" className="text-[#ff9906] hover:underline flex items-center">
            View All Orders
          </Link>

          <div className="flex gap-4">
            <Link
              to="/goods"
              className="bg-white border border-[#ff9906] text-[#ff9906] hover:bg-[#fff8ee] px-4 py-2 rounded-md font-medium transition-colors"
            >
              Continue Shopping
            </Link>
            <Link
              to="/contact-us"
              className="bg-[#ff9906] hover:bg-[#e68a00] text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center"
            >
              Need Help?
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
